import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiChevronRight,
  FiHome,
  FiChevronLeft,
  FiChevronRight as FiNext,
  FiDollarSign,
} from "react-icons/fi";
import { toast } from "react-toastify";

import { getAutoPoolUsers, bulkprepay, getAllAutoPoolPayment } from "../../store/slices/pool";

const AutoPoolDetails = () => {
  const dispatch = useDispatch();

  const { id } = useParams();


  const [currentPage, setCurrentPage] = useState(1);
  const [bulkPrepayLoading, setBulkPrepayLoading] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const limit = 25;

  /* =====================================================
     REDUX
  ===================================================== */

  const {
    autoPoolUsers = [],
    autoPoolUsersData,
    autoPoolUsersLoading,
    error,
  } = useSelector(
    (state) => state.pool || {}
  );

  /* =====================================================
     GET USERS
  ===================================================== */

  useEffect(() => {
    if (!id) {
      return;
    }

    dispatch(
      getAutoPoolUsers({
        pool: id,
        page: currentPage,
        limit,
      })
    );
  }, [dispatch, id, currentPage]);

  /* =====================================================
     RESET PAGE WHEN POOL CHANGES
  ===================================================== */

  useEffect(() => {
    setCurrentPage(1);
  }, [id]);

  /* =====================================================
     POOL INFO
  ===================================================== */

  const poolLevel =
    autoPoolUsersData?.filters?.pool || id;

  const poolName = `Auto Pool ${poolLevel}`;

  /* =====================================================
     PAGINATION DATA
  ===================================================== */

  const pagination =
    autoPoolUsersData?.pagination || {};

  const totalUsers =
    Number(pagination?.totalUsers) || 0;

  const totalPages =
    Number(pagination?.totalPages) || 0;

  const apiCurrentPage =
    Number(pagination?.currentPage) ||
    currentPage;

  const apiLimit =
    Number(pagination?.limit) || limit;

  const hasNextPage =
    pagination?.hasNextPage ??
    (apiCurrentPage < totalPages);

  const hasPreviousPage =
    pagination?.hasPreviousPage ??
    (apiCurrentPage > 1);

  /* =====================================================
     FORMAT DATE
  ===================================================== */

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  /* =====================================================
     USERS
  ===================================================== */

  const users = Array.isArray(autoPoolUsers)
    ? autoPoolUsers
    : [];

  /* =====================================================
     PAGE CHANGE
  ===================================================== */

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber < 1 ||
      pageNumber > totalPages ||
      pageNumber === currentPage ||
      autoPoolUsersLoading
    ) {
      return;
    }

    setCurrentPage(pageNumber);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =====================================================
     PREVIOUS
  ===================================================== */

  const handlePrevious = () => {
    if (
      hasPreviousPage &&
      !autoPoolUsersLoading
    ) {
      handlePageChange(
        apiCurrentPage - 1
      );
    }
  };

  /* =====================================================
     NEXT
  ===================================================== */

  const handleNext = () => {
    if (
      hasNextPage &&
      !autoPoolUsersLoading
    ) {
      handlePageChange(
        apiCurrentPage + 1
      );
    }
  };

  /* =====================================================
     PAGINATION NUMBERS
  ===================================================== */

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (
        let i = 1;
        i <= totalPages;
        i++
      ) {
        pages.push(i);
      }

      return pages;
    }

    if (apiCurrentPage <= 3) {
      pages.push(
        1,
        2,
        3,
        4,
        "...",
        totalPages
      );

      return pages;
    }

    if (
      apiCurrentPage >=
      totalPages - 2
    ) {
      pages.push(
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );

      return pages;
    }

    pages.push(
      1,
      "...",
      apiCurrentPage - 1,
      apiCurrentPage,
      apiCurrentPage + 1,
      "...",
      totalPages
    );

    return pages;
  };

  /* =====================================================
     DISPLAY RANGE
  ===================================================== */

  const displayStart =
    totalUsers > 0
      ? (apiCurrentPage - 1) *
      apiLimit +
      1
      : 0;

  const displayEnd =
    Math.min(
      apiCurrentPage * apiLimit,
      totalUsers
    );

  const handleBulkPrePay = async () => {
    if (!poolLevel) {
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to bulk pre-pay all eligible users in Auto Pool ${poolLevel}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setBulkPrepayLoading(true);

      const result = await dispatch(
        bulkprepay({
          poolLevel: Number(poolLevel),
        })
      ).unwrap();

      alert(
        result?.message ||
        "Bulk pre-payment completed successfully"
      );

      // Refresh users
      dispatch(
        getAutoPoolUsers({
          pool: id,
          page: currentPage,
          limit,
        })
      );
    } catch (error) {
      console.error("Bulk pre-pay error:", error);

      alert(
        error?.message ||
        error ||
        "Failed to process bulk pre-payment"
      );
    } finally {
      setBulkPrepayLoading(false);
    }
  };

  const handleDownloadPaymentReport = async () => {
    try {
      const paidPool = Number(poolLevel);

      if (!paidPool) {
        toast.error("Paid pool is required");
        return;
      }

      setDownloadingReport(true);

      await dispatch(
        getAllAutoPoolPayment({
          paidPool,
          download: 1,
        })
      ).unwrap();

      toast.success(
        "Payment report has been sent to your email successfully"
      );
    } catch (error) {
      console.error("DOWNLOAD PAYMENT REPORT ERROR:", error);

      toast.error(
        error || "Failed to send payment report"
      );
    } finally {
      setDownloadingReport(false);
    }
  };

  return (
    <div className="gi-referral-page">

      {/* =================================================
          BREADCRUMB
      ================================================= */}

      <div className="gi-breadcrumb">

        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight
          className="gi-breadcrumb-arrow"
        />

        <Link to="/dashboard/autopool-income">
          Auto Pool
        </Link>

        <FiChevronRight
          className="gi-breadcrumb-arrow"
        />

        <span>
          {poolName}
        </span>

      </div>
      <div
        className="d-flex justify-content-between align-items-center mb-3"
        style={{
          padding: "15px 20px",
          borderBottom: "1px solid #eee",
        }}
      >
        <div>
          <h5 className="mb-1">
            {poolName}
          </h5>

          <small className="text-muted">
            {totalUsers} eligible users
          </small>
        </div>

        {/*   <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={handleBulkPrePay}
          disabled={
            bulkPrepayLoading ||
            totalUsers === 0
          }
        >
          <FiDollarSign size={16} />

          {bulkPrepayLoading
            ? "Processing..."
            : "Bulk Pre-Pay"}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleDownloadPaymentReport}
          disabled={downloadingReport}
        >
          {downloadingReport
            ? "Sending Report..."
            : "Download Payment Report"}
        </button>
        */}
      </div>

      {/* =================================================
          TABLE CARD
      ================================================= */}

      <section className="gi-referral-table-card">

        <div className="gi-referral-table-wrapper">

          {/* =================================================
              LOADING
          ================================================= */}

          {autoPoolUsersLoading ? (

            <div className="gi-pool-members-loading">
              Loading members...
            </div>

          ) : error ? (

            /* =================================================
               ERROR
            ================================================= */

            <div className="gi-pool-members-loading">
              {error}
            </div>

          ) : users.length === 0 ? (

            /* =================================================
               NO DATA
            ================================================= */

            <div className="gi-pool-members-loading">
              No members found in {poolName}
            </div>

          ) : (

            <>

              {/* =================================================
                  TABLE
              ================================================= */}

              <table className="gi-referral-table">

                <thead>

                  <tr>

                    <th>
                      #
                    </th>

                    <th>
                      Activation No
                    </th>

                    <th>
                      User ID
                    </th>

                    <th>
                      Name
                    </th>

                    <th>
                      Mobile
                    </th>

                    <th>
                      Email
                    </th>

                    <th>
                      Status
                    </th>

                    <th>
                      Activation Date
                    </th>
                    {/* <th>
                      Action
                    </th> */}

                  </tr>

                </thead>


                <tbody>

                  {users.map(
                    (user, index) => {

                      const serialNumber =
                        (apiCurrentPage - 1) *
                        apiLimit +
                        index +
                        1;

                      return (

                        <tr
                          key={
                            user._id ||
                            user.userId ||
                            index
                          }
                        >

                          {/* # */}

                          <td>
                            {String(
                              serialNumber
                            ).padStart(2, "0")}
                          </td>


                          {/* ACTIVATION NO */}

                          <td>
                            {user.autoPoolActivationNo ??
                              "-"}
                          </td>


                          {/* USER ID */}

                          <td>
                            <strong>
                              {user.userId ||
                                "-"}
                            </strong>
                          </td>


                          {/* NAME */}

                          <td>
                            {user.name ||
                              "-"}
                          </td>


                          {/* MOBILE */}

                          <td>
                            {user.mobileNo ||
                              "-"}
                          </td>


                          {/* EMAIL */}

                          <td>
                            {user.email ||
                              "-"}
                          </td>


                          {/* STATUS */}

                          <td>

                            <span
                              className={
                                user.status ===
                                  "ACTIVE"
                                  ? "badge bg-success"
                                  : "badge bg-secondary"
                              }
                            >
                              {user.status ||
                                "-"}
                            </span>

                          </td>


                          {/* ACTIVATION DATE */}

                          <td>
                            {formatDate(
                              user.activationDate
                            )}
                          </td>
                          <td>
                            <Link
                              to={`/dashboard/prepay-autopool/${user.userId}`}
                              className="gi-pool-members-btn active gap-1" style={{ color: "#fff" }}
                            >
                              <FiDollarSign size={14} />
                              Pre-Pay
                            </Link>
                          </td>

                        </tr>

                      );

                    }
                  )}

                </tbody>

              </table>


              {/* =================================================
                  PAGINATION
              ================================================= */}

              {totalPages > 0 && (

                <div className="gi-users-pagination">

                  {/* PAGINATION INFO */}

                  <div className="gi-pagination-info">

                    Showing{" "}

                    <strong>
                      {displayStart}
                    </strong>

                    {" "}to{" "}

                    <strong>
                      {displayEnd}
                    </strong>

                    {" "}of{" "}

                    <strong>
                      {totalUsers}
                    </strong>

                    {" "}users

                  </div>


                  {/* PAGINATION BUTTONS */}

                  <div className="gi-pagination-buttons">

                    {/* PREVIOUS */}

                    <button
                      type="button"
                      className="gi-pagination-btn"
                      disabled={
                        !hasPreviousPage ||
                        autoPoolUsersLoading
                      }
                      onClick={
                        handlePrevious
                      }
                    >

                      <FiChevronLeft />

                      <span>
                        Prev
                      </span>

                    </button>


                    {/* PAGE NUMBERS */}

                    {getPageNumbers().map(
                      (
                        pageNumber,
                        index
                      ) => {

                        if (
                          pageNumber ===
                          "..."
                        ) {
                          return (
                            <span
                              key={`dots-${index}`}
                              className="gi-pagination-dots"
                            >
                              ...
                            </span>
                          );
                        }

                        return (

                          <button
                            key={pageNumber}
                            type="button"
                            className={`gi-pagination-number ${apiCurrentPage ===
                              pageNumber
                              ? "active"
                              : ""
                              }`}
                            disabled={
                              autoPoolUsersLoading
                            }
                            onClick={() =>
                              handlePageChange(
                                pageNumber
                              )
                            }
                          >
                            {pageNumber}
                          </button>

                        );

                      }
                    )}


                    {/* NEXT */}

                    <button
                      type="button"
                      className="gi-pagination-btn"
                      disabled={
                        !hasNextPage ||
                        autoPoolUsersLoading
                      }
                      onClick={
                        handleNext
                      }
                    >

                      <span>
                        Next
                      </span>

                      <FiNext />

                    </button>

                  </div>

                </div>

              )}

            </>

          )}

        </div>

      </section>

    </div>
  );
};

export default AutoPoolDetails;